import { Injectable, inject } from '@angular/core';

import { Observable, map, of } from 'rxjs';

import { ConfigurationService } from '@sondermind/configuration';
import { Logger } from '@sondermind/datadog-logging';
import { HttpService } from '@sondermind/http';
import { camelCaseTransformer } from '@sondermind/utilities/tools';

import {
  IPaymentMethod,
  IPaymentMethodApiResponse,
  IPaymentMethodCreateRequest,
  IPaymentMethodSingleApiResponse,
  ISetupIntentCreateRequest,
  ISetupIntentApiResponse,
  ISetupIntentBackendResponse
} from '../models/payment-method.model';

@Injectable({
  providedIn: 'root'
})
export class PaymentMethodHttpService {
  private readonly http = inject(HttpService);
  private readonly config = inject(ConfigurationService);
  private readonly logger = inject(Logger);

  private readonly paymentMethodsUrl = `${this.config.env.apiBase}/v2/payment_methods`;
  private readonly setupIntentsUrl = `${this.config.env.apiBase}/v2/setup_intents`;

  /**
   * Save a validated payment method to the backend.
   * This is typically called after Stripe confirms the payment method via SetupIntent.
   *
   * @returns Observable<IPaymentMethod | null>
   * Emits the created payment method on success, or null on any failure
   * (validation, HTTP error, malformed response).
   */
  create(
    stripePaymentMethodId: string,
    nickname: string,
    accountSetupContext: string | null = null,
    paymentMethodType?: string
  ): Observable<IPaymentMethod | null> {
    // Validate required inputs - emit null instead of throwing
    if (!stripePaymentMethodId?.trim()) {
      this.logger.error('create called with empty stripePaymentMethodId');
      return of(null);
    }

    const body: IPaymentMethodCreateRequest = {
      payment_method: {
        stripe_payment_method_id: stripePaymentMethodId,
        nickname,
        // Only include payment_method_type if defined
        ...paymentMethodType && {
          payment_method_type: paymentMethodType
        }
      },
      account_setup_context: accountSetupContext
    };

    return this.http
      .post<IPaymentMethodSingleApiResponse>(this.paymentMethodsUrl, body)
      .pipe(
        map((response) => {
          // API returns { credit_card: {...} } or { creditCard: {...} }
          // TypeScript interface only defines credit_card, but backend may return camelCase
          const paymentMethodData = response?.credit_card ||
            (response as IPaymentMethodSingleApiResponse & { creditCard?: IPaymentMethodApiResponse })?.creditCard;
          if (!paymentMethodData) {
            this.logger.error('Payment method create returned malformed response', { response });
            return null;
          }
          return camelCaseTransformer<IPaymentMethod>(paymentMethodData);
        }),
        this.http.handleHttpError()
      );
  }

  /**
   * Create a Stripe SetupIntent for saving payment methods without charging.
   *
   * Returns a client_secret that you pass to stripe.confirmSetup() or
   * stripe.confirmCardSetup() in the frontend.
   *
   * **Why no `patient_id` parameter?**
   * SetupIntents are created against the authenticated user's Stripe customer account.
   * The `persona_client_id` is only used for provider-facing endpoints where providers
   * create payment methods on behalf of their clients. If persona_client_id is absent,
   * the SetupIntent is created for the currently authenticated user.
   *
   * The asymmetry with the save path (add-cc-modal line 190-195) is intentional:
   * - SetupIntent creation: uses authenticated user's Stripe customer
   * - Payment method save: may need explicit patient_id for legacy endpoints
   *
   * @param paymentMethodTypes - Optional array of payment types to support (defaults to ['card'])
   * @param usage - 'off_session' for recurring payments, 'on_session' for user-present flows
   * @param personaClientId - Optional client ID when a provider is creating a payment method for their client
   * @returns Observable with client_secret and SetupIntent ID
   */
  createSetupIntent(
    paymentMethodTypes: string[] = ['card'],
    usage: 'off_session' | 'on_session' = 'off_session',
    personaClientId?: number
  ): Observable<ISetupIntentApiResponse | null> {
    const body: ISetupIntentCreateRequest = {
      payment_method_types: paymentMethodTypes,
      usage,
      // Explicit null/undefined check to handle personaClientId === 0 correctly
      ...(personaClientId != null ? { persona_client_id: personaClientId } : {})
    };

    return this.http
      .post<ISetupIntentBackendResponse>(this.setupIntentsUrl, body)
      .pipe(
        map((response) => {
          // Backend returns { setup_intent: { client_secret, id, status } }
          // Extract and transform the setup_intent object
          const backendIntent = response?.setup_intent;
          if (!backendIntent?.client_secret) {
            this.logger.error('SetupIntent create returned malformed response', { response });
            return null;
          }

          // Transform to match frontend interface (camelCase)
          const setupIntent: ISetupIntentApiResponse = {
            /* eslint-disable @typescript-eslint/naming-convention */
            client_secret: backendIntent.client_secret,
            /* eslint-enable @typescript-eslint/naming-convention */
            id: backendIntent.id,
            status: backendIntent.status,
          };

          return setupIntent;
        }),
        this.http.handleHttpError()
      );
  }
}
